import * as React from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { US_TIMEZONES } from "@/lib/campaign";
import { contactName } from "@/lib/contacts";
import { useFormChanged, zodResolver } from "@/lib/form";
import {
  useCampaigns, useCreateContact, useFirms, useUpdateContact, type ContactCreateInput, type ContactInput,
} from "@/lib/queries";
import { atLeast, type LeadCard } from "@/lib/types";

/**
 * Create or edit a contact (ADR-044).
 *
 * Who edits what mirrors core-api's `PATCH /leads/{id}`: a rep the names and the language; a supervisor
 * also the phone, the residence state, the timezone (each leaves a system note — they drive quiet
 * hours), the e-mail and the firm. Nobody the consent, the source, the stage or the dialer's columns,
 * so none of those is a field. The form shows a rep the supervisor's fields disabled rather than
 * hiding them: the record says the phone is there and a rep should see why they cannot change it.
 *
 * A phone that already has a record is a 409 naming it; the form offers the record, and "Create
 * anyway" for the case the design allows — a second record for another firm, as GoHighLevel kept
 * them.
 */

const NONE = "none";

const schema = z.object({
  phone: z.string().min(3, "A number is required").max(40),
  first_name: z.string().max(120),
  last_name: z.string().max(120),
  email: z.string().max(254).refine((v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), "That does not look like an e-mail"),
  language: z.string(),
  state: z.string().refine((v) => !v || /^[A-Za-z]{2}$/.test(v), "Two letters, like CA"),
  timezone: z.string(),
  firm_id: z.string(),
  campaign_id: z.string(),
  consent_wording: z.string().max(4000),
  consent_captured_at: z.string(),
}).superRefine((value, ctx) => {
  if (Boolean(value.consent_wording.trim()) !== Boolean(value.consent_captured_at)) {
    ctx.addIssue({
      code: "custom",
      path: [value.consent_wording.trim() ? "consent_captured_at" : "consent_wording"],
      message: "Consent is the wording and when it was captured, both or neither",
    });
  }
});

type FormValues = z.infer<typeof schema>;

function valuesOf(contact: LeadCard | undefined): FormValues {
  return {
    phone: contact?.phone ?? "",
    first_name: contact?.first_name ?? "",
    last_name: contact?.last_name ?? "",
    email: contact?.email ?? "",
    language: contact?.language ?? NONE,
    state: contact?.state ?? "",
    timezone: contact?.timezone ?? NONE,
    firm_id: contact?.firm ? String(contact.firm.id) : NONE,
    campaign_id: NONE,
    consent_wording: "",
    consent_captured_at: "",
  };
}

const orNull = (v: string) => (v.trim() ? v.trim() : null);
const pick = (v: string) => (v === NONE ? null : v);

export function ContactFormDialog({
  open, onOpenChange, contact, onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  contact?: LeadCard;
  onCreated?: (card: LeadCard) => void;
}) {
  const editing = Boolean(contact);
  const { user } = useAuth();
  const supervisor = atLeast(user?.role, "supervisor");
  const toast = useToast();
  const create = useCreateContact();
  const update = useUpdateContact();
  const firms = useFirms({ enabled: open });
  const campaigns = useCampaigns({}, { enabled: open && !editing && supervisor });
  const [formError, setFormError] = React.useState<string | null>(null);
  const [existing, setExisting] = React.useState<string | null>(null);

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: valuesOf(contact) });
  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = form;

  React.useEffect(() => {
    if (!open) return;
    reset(valuesOf(contact));
    setFormError(null);
    setExisting(null);
  }, [open, contact, reset]);

  const changed = useFormChanged(form);
  const busy = create.isPending || update.isPending;

  const submit = (values: FormValues, duplicate = false) => {
    setFormError(null);
    setExisting(null);
    if (editing && contact) {
      const input: ContactInput & { id: string } = { id: contact.id };
      if (values.first_name.trim() !== (contact.first_name ?? "")) input.first_name = orNull(values.first_name);
      if (values.last_name.trim() !== (contact.last_name ?? "")) input.last_name = orNull(values.last_name);
      if (pick(values.language) !== contact.language) input.language = pick(values.language);
      if (supervisor) {
        if (values.phone.trim() !== contact.phone) input.phone = values.phone.trim();
        if ((values.state.trim().toUpperCase() || null) !== contact.state) input.state = orNull(values.state.toUpperCase());
        if (pick(values.timezone) !== contact.timezone) input.timezone = pick(values.timezone);
        if (values.email.trim() !== (contact.email ?? "")) input.email = orNull(values.email);
        const firm = values.firm_id === NONE ? null : Number(values.firm_id);
        if (firm !== null && firm !== (contact.firm?.id ?? null)) input.firm_id = firm;
      }
      if (Object.keys(input).length === 1) {
        onOpenChange(false);
        return;
      }
      update.mutate(input, {
        onSuccess: () => {
          toast.success("Contact saved");
          onOpenChange(false);
        },
        onError: (e) => setFormError(e instanceof ApiError ? e.message : "Could not save the contact."),
      });
      return;
    }
    const input: ContactCreateInput = {
      phone: values.phone.trim(),
      first_name: orNull(values.first_name),
      last_name: orNull(values.last_name),
      email: orNull(values.email),
      language: pick(values.language),
      state: orNull(values.state.toUpperCase()),
      timezone: pick(values.timezone),
      firm_id: values.firm_id === NONE ? null : Number(values.firm_id),
      campaign_id: pick(values.campaign_id),
      consent_wording: orNull(values.consent_wording),
      consent_captured_at: values.consent_captured_at ? new Date(values.consent_captured_at).toISOString() : null,
      duplicate,
    };
    create.mutate(input, {
      onSuccess: (card) => {
        toast.success("Contact created", contactName(card) ?? card.phone);
        onOpenChange(false);
        onCreated?.(card);
      },
      onError: (e) => {
        const body = e instanceof ApiError ? (e.body as { code?: string; existing_lead_id?: string } | undefined) : undefined;
        if (body?.code === "phone_exists" && body.existing_lead_id) {
          setExisting(body.existing_lead_id);
          return;
        }
        setFormError(e instanceof ApiError ? e.message : "Could not create the contact.");
      },
    });
  };

  const lockedHint = supervisor ? undefined : "A supervisor changes this";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer" size="md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit contact" : "New contact"}</DialogTitle>
          <DialogDescription>
            {editing
              ? "The facts a person may change. The stage, the consent and the dialer's own state are not among them."
              : "A contact typed in here starts as New. Without consent evidence the dialer will not call them."}
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={handleSubmit((values) => submit(values))}>
          <DialogBody className="space-y-4">
            <DiscardGuard changed={changed} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name" htmlFor="contact-first" error={errors.first_name?.message}>
                <Input {...register("first_name")} autoComplete="off" />
              </Field>
              <Field label="Last name" htmlFor="contact-last" error={errors.last_name?.message}>
                <Input {...register("last_name")} autoComplete="off" />
              </Field>
            </div>
            <Field
              label="Phone"
              htmlFor="contact-phone"
              required
              error={errors.phone?.message}
              hint={editing ? lockedHint : "Ten digits, or E.164 with the country code"}
            >
              <Input {...register("phone")} inputMode="tel" disabled={editing && !supervisor} placeholder="(310) 555-0142" />
            </Field>
            <Field label="E-mail" htmlFor="contact-email" error={errors.email?.message} hint={editing ? lockedHint : undefined}>
              <Input {...register("email")} type="email" disabled={editing && !supervisor} />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Language" htmlFor="contact-language">
                <Select value={watch("language")} onValueChange={(v) => setValue("language", v, { shouldDirty: true })}>
                  <SelectTrigger id="contact-language" aria-label="Language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Not set</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Residence state" htmlFor="contact-state" error={errors.state?.message} hint={editing ? lockedHint : undefined}>
                <Input {...register("state")} maxLength={2} placeholder="CA" className="uppercase" disabled={editing && !supervisor} />
              </Field>
              <Field label="Timezone" htmlFor="contact-timezone" hint={editing ? lockedHint : undefined}>
                <Select
                  value={watch("timezone")}
                  onValueChange={(v) => setValue("timezone", v, { shouldDirty: true })}
                  disabled={editing && !supervisor}
                >
                  <SelectTrigger id="contact-timezone" aria-label="Timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>From the state</SelectItem>
                    {US_TIMEZONES.map((tz) => (
                      <SelectItem key={tz.zone} value={tz.zone}>
                        {tz.label}
                      </SelectItem>
                    ))}
                    {contact?.timezone && !US_TIMEZONES.some((tz) => tz.zone === contact.timezone) && (
                      <SelectItem value={contact.timezone}>{contact.timezone}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            {supervisor && (
              <Field
                label="Firm"
                htmlFor="contact-firm"
                hint={editing ? "A contact keeps a firm once it has one; it can be changed, not cleared." : undefined}
              >
                <Select value={watch("firm_id")} onValueChange={(v) => setValue("firm_id", v, { shouldDirty: true })}>
                  <SelectTrigger id="contact-firm" aria-label="Firm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(!editing || !contact?.firm) && <SelectItem value={NONE}>No firm yet</SelectItem>}
                    {(firms.data ?? []).map((firm) => (
                      <SelectItem key={firm.id} value={String(firm.id)}>
                        {firm.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {!editing && supervisor && (
              <Field label="Put on a campaign's list" htmlFor="contact-campaign" hint="The calling campaign, usually. A do-not-call or signed record with the phone, or a paused firm, keeps it off.">
                <Select value={watch("campaign_id")} onValueChange={(v) => setValue("campaign_id", v, { shouldDirty: true })}>
                  <SelectTrigger id="contact-campaign" aria-label="Campaign">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No list</SelectItem>
                    {(campaigns.data ?? []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {!editing && (
              <>
                <Field
                  label="Consent wording"
                  htmlFor="contact-consent"
                  error={errors.consent_wording?.message}
                  hint="The words the person agreed to, exactly. Immutable once saved."
                >
                  <Textarea {...register("consent_wording")} rows={2} className="font-sans text-sm" />
                </Field>
                <Field label="Consent captured" htmlFor="contact-consent-at" error={errors.consent_captured_at?.message}>
                  <Input {...register("consent_captured_at")} type="datetime-local" />
                </Field>
              </>
            )}
            {existing && (
              <Callout tone="warning">
                A contact with this phone exists:{" "}
                <Link to={`/contacts/${existing}`} className="underline" onClick={() => onOpenChange(false)}>
                  open it
                </Link>
                . Create a second record only for another firm.
                <div className="mt-2">
                  <Button type="button" size="sm" variant="outline" loading={busy} onClick={handleSubmit((v) => submit(v, true))}>
                    Create anyway
                  </Button>
                </div>
              </Callout>
            )}
            {formError && <Callout tone="danger">{formError}</Callout>}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={busy}>
              {editing ? "Save" : "Create contact"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
